import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Check, Trash2, Clock, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { api, errMsg, fmtDate } from "@/lib/api";

export default function AdminCategories() {
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/categories").then((r) => setCats(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/categories", { name });
      toast.success("Category added");
      setOpen(false); setName("");
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  const approve = async (c) => {
    try { await api.post(`/admin/categories/${c.id}/approve`); toast.success(`Approved "${c.name}"`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (c) => {
    try { await api.delete(`/admin/categories/${c.id}`); toast.success("Category removed"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const pending = cats.filter((c) => c.status === "pending");
  const approved = cats.filter((c) => c.status === "approved");

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="small-label">Control Panel</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Categories</h1>
          <p className="text-sm text-slate-500 mt-2 max-w-xl">
            Students only see tests whose category matches theirs. Approve student-proposed categories to make them available to everyone.
          </p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="admin-add-category-button" className="bg-[#2563EB] hover:bg-[#1D4ED8] hover:text-white">
          <Plus className="h-4 w-4 mr-1.5" />New category
        </Button>
      </div>

      {pending.length > 0 && (
        <section className="mt-8" data-testid="admin-pending-categories">
          <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
            <Clock className="h-4 w-4 text-amber-600" />Awaiting approval
            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 ml-1">{pending.length}</Badge>
          </h2>
          <div className="mt-3 bg-white border border-slate-200 rounded-xl overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Proposed by</TableHead><TableHead>Proposed</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {pending.map((c) => (
                  <TableRow key={c.id} data-testid={`admin-pending-category-${c.id}`}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell className="text-slate-500">{c.proposer ? `${c.proposer.name} · ${c.proposer.email}` : "—"}</TableCell>
                    <TableCell className="text-slate-500">{fmtDate(c.created_at)}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button size="sm" onClick={() => approve(c)} data-testid={`admin-approve-category-${c.id}`} className="bg-green-600 hover:bg-green-700 text-white mr-1">
                        <Check className="h-4 w-4 mr-1" />Approve
                      </Button>
                      <ConfirmDelete testId={`admin-reject-category-${c.id}`} title={`Reject "${c.name}"?`} description="The student who proposed this will lose this category selection."
                        onConfirm={() => remove(c)}
                        trigger={<Button size="icon" variant="ghost" className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`admin-reject-category-${c.id}`}><Trash2 className="h-4 w-4" /></Button>} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>
      )}

      <section className="mt-10" data-testid="admin-approved-categories">
        <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-green-600" />Approved categories
        </h2>
        <div className="mt-3 bg-white border border-slate-200 rounded-xl overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Tests</TableHead><TableHead>Students</TableHead><TableHead>Added</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {approved.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-10 text-slate-500">No approved categories yet.</TableCell></TableRow>}
              {approved.map((c) => (
                <TableRow key={c.id} data-testid={`admin-approved-category-${c.id}`}>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell className="font-mono">{c.tests_count}</TableCell>
                  <TableCell className="font-mono">{c.students_count}</TableCell>
                  <TableCell className="text-slate-500">{fmtDate(c.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <ConfirmDelete testId={`admin-delete-category-${c.id}`} title={`Delete "${c.name}"?`}
                      description={`This removes the category from ${c.tests_count} test(s) and clears it from ${c.students_count} student(s).`}
                      onConfirm={() => remove(c)}
                      trigger={<Button size="icon" variant="ghost" className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`admin-delete-category-${c.id}`}><Trash2 className="h-4 w-4" /></Button>} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="admin-category-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">New category</DialogTitle>
            <DialogDescription>Create an approved category that students and tests can be assigned to.</DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. GATE" data-testid="admin-category-name-input" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving} data-testid="admin-save-category-button" className="bg-[#2563EB] hover:bg-[#1D4ED8] hover:text-white">
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
